import React, { useEffect, useState } from "react";
import axios from "axios";
import { connect } from "react-redux";
import { Link, useHistory } from "react-router-dom";
import {
  Building2,
  MapPin,
  ArrowLeft,
  Save,
  Info,
  ShieldCheck,
  Edit3,
  Globe,
  Tag
} from "lucide-react";
import { Formik, Form, Field } from "formik";
import { editSite } from "../../store/actions";
import {
  Button,
  Input,
  Select,
  Tabs,
  Breadcrumb,
  useToast,
  Card
} from "../UI";
import { axiosConfig } from "../../config/index";
import { SiteValidationSchema } from "../../validations/index";

const EditSite = ({ editSite, singleSite }: any) => {
  const history = useHistory();
  const { toast } = useToast();
  const [locationItems, setLocationItems] = useState<any>([]);

  useEffect(() => {
    axios.get(`/api/sites/getalllocation/`, axiosConfig)
      .then((response: any) => {
        setLocationItems(response.data);
      })
      .catch((err: any) => console.error(err));
  }, []);

  if (!singleSite) {
    history.push("/sitelist");
    return null;
  }

  const locationOptions = locationItems.map((op: any) => ({
    label: op.location,
    value: op.location
  }));

  const tabs = [
    {
      id: 'details',
      icon: <Edit3 className="w-4 h-4" />,
      content: (
        <div className="space-y-8 animate-in fade-in slide-in-from-bottom-2 duration-500">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="space-y-6">
              <Field name="sitename">
                {({ field, form, meta }: any) => (
                  <Input
                    {...field}
                    label="Site Name"
                    placeholder="e.g. Corporate Headquarters"
                    error={meta.touched && meta.error ? meta.error : ""}
                    required
                  />
                )}
              </Field>

              <Field name="region">
                {({ field, form, meta }: any) => (
                  <Select
                    label="Location / Region"
                    placeholder="Select location..."
                    options={locationOptions}
                    value={field.value}
                    onChange={(val) => form.setFieldValue("region", val)}
                    error={meta.touched && meta.error ? meta.error : ""}
                    searchable
                    required
                  />
                )}
              </Field>
            </div>

            <div className="space-y-6">
              <div className="p-4 bg-[#8dc63f]/10/50 rounded-2xl border border-[#8dc63f]/20 flex gap-4">
                <div className="p-2 bg-white rounded-xl shadow-sm h-fit">
                  <Globe className="w-4 h-4 text-[#8dc63f]" />
                </div>
                <div className="space-y-1">
                  <p className="text-sm font-bold text-slate-700">Regional Mapping</p>
                  <p className="text-xs font-medium text-[#8dc63f]/70 leading-relaxed">
                    Selecting the correct region ensures that local security managers receive approval notifications for this site.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    }
  ];

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      <div className="flex flex-col gap-4">
        <Breadcrumb
          items={[
            { label: 'Home', to: '/dashboard' },
            { label: 'Sites', to: '/sitelist' },
            { label: 'Edit Site' }
          ]}
        />

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-[#8dc63f] rounded-2xl shadow-lg shadow-[#8dc63f]/30">
              <Building2 className="w-6 h-6 text-white" />
            </div>

            <h1 className="text-2xl font-extrabold text-slate-900 tracking-tight leading-none">Edit Site</h1>

          </div>

          <Link to="/sitelist">
            <Button variant="ghost" className="gap-2 text-slate-400 hover:text-slate-900">
              <ArrowLeft className="w-4 h-4" />
              Back to List
            </Button>
          </Link>
        </div>
      </div>

      <Formik
        initialValues={{
          sitename: singleSite?.sitename,
          region: singleSite?.region,
        }}
        enableReinitialize={true}
        validationSchema={SiteValidationSchema}
        onSubmit={async (values, { setErrors, setSubmitting }) => {
          try {
            await editSite(singleSite.id || singleSite._id, values);
            toast("Site updated successfully", "success");
            history.push("/sitelist");
          } catch (error: any) {
            toast(error.message || "Failed to update site", "error");
            setErrors(error);
          } finally {
            setSubmitting(false);
          }
        }}
      >
        {({ handleSubmit, isSubmitting }) => (
          <form onSubmit={handleSubmit} className="space-y-8">
            <Card>
              {tabs[0].content}
            </Card>

            <div className="flex items-center justify-end gap-3 pt-4 px-1">
              <Link to="/sitelist">
                <Button type="button" variant="ghost" className="h-12 px-8">Cancel</Button>
              </Link>
              <Button
                type="submit"
                className="h-10 px-10 gap-2 shadow-xl shadow-[#8dc63f]/20 bg-[#8dc63f] hover:bg-[#76ab32]"
                disabled={isSubmitting}
              >
                <Save className="w-4 h-4" />
                {isSubmitting ? 'Saving Changes...' : 'Update Site'}
              </Button>
            </div>
          </form>
        )}
      </Formik>
    </div>
  );
};

const mapStateToProps = (state: any) => ({
  singleSite: state.site.singleSite,
});

const mapDispatchToProps = {
  editSite,
};

export default connect(mapStateToProps, mapDispatchToProps)(EditSite);



